import React from "react";

import {
  List,
  DataTable,
  TextField,
  TextInput,
  SimpleForm,
  Create,
  Edit,
  Show,
  TabbedShowLayout,
  Tab,
  useRecordContext,
  required,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  FilterButton,
  Pagination,
  FunctionField,
} from "react-admin";
import { DetailsButton } from "../lib/detailsButton";
import { CustomTab } from "../lib/customTab";
import { hasScope, SCOPE } from "../lib/auth";

// Accepts the standard field props (label/sortable) that react-admin passes
// to fields; this component ignores them but must accept them for callers.
export const AnalysisTypeSource = (_props: {
  label?: string;
  sortable?: boolean | "asc" | "desc";
}) => {
  const rec = useRecordContext();

  if (rec?.custom?.vintraceMetric) {
    return (
      <FunctionField
        render={() => (
          <>
            Vintrace{" "}
            <span style={{ opacity: 0.3 }}>
              {"Metric "}
              {rec?.custom?.vintraceMetric.id}:{" "}
              {rec?.custom?.vintraceMetric.name}
            </span>
          </>
        )}
      />
    );
  }

  if (rec?.custom?.innovintAnalysisType) {
    return (
      <FunctionField
        render={() => (
          <>
            InnoVint{" "}
            <span style={{ opacity: 0.3 }}>
              {rec?.custom?.innovintAnalysisType?.name}{" "}
              {rec?.custom?.innovintAnalysisUnit?.unit}
            </span>
          </>
        )}
      />
    );
  }

  return <FunctionField render={() => "\u00A0"} />;
};

export const AnalysisTypeList = () => (
  <List
    perPage={100}
    sort={{ field: "analyte", order: "ASC" }}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={[
      <TextInput label="Analyte" source="analyte" />,
      <TextInput label="Unit" source="unit" />,
      <TextInput label="Method" source="method" />,
    ]}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton disabled={!hasScope(SCOPE.MANAGER)} />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="analyte" label="Analyte" disableSort>
        <TextField source="analyte" label="Analyte" />
      </DataTable.Col>
      <DataTable.Col source="unit" label="Unit" disableSort>
        <TextField source="unit" label="Unit" />
      </DataTable.Col>
      <DataTable.Col source="method" label="Method" disableSort>
        <TextField source="method" label="Method" />
      </DataTable.Col>
      <DataTable.Col source="source" label="Source" disableSort>
        <AnalysisTypeSource label="Source" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const AnalysisTypeEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>Analysis Type {record.id}</span>;
};

const AnalysisTypeCreateEditForm = () => (
  <SimpleForm>
    <TextInput label="Analyte" source="analyte" validate={required()} />
    <TextInput label="Unit" source="unit" />
    <TextInput label="Method" source="method" />
  </SimpleForm>
);

export const AnalysisTypeEdit = () => (
  <Edit title={<AnalysisTypeEditTitle />}>
    <AnalysisTypeCreateEditForm />
  </Edit>
);

export const AnalysisTypeCreate = () => (
  <Create>
    <AnalysisTypeCreateEditForm />
  </Create>
);

const AnalysisTypeShowTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>Analysis Type {record.id}</span>;
};

export const AnalysisTypeShow = () => (
  <Show title={<AnalysisTypeShowTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <TextField source="id" label="ID" />
        <TextField source="analyte" label="Analyte" />
        <TextField source="unit" label="Unit" emptyText={"\u00A0"} />
        <TextField source="method" label="Method" emptyText={"\u00A0"} />
        <AnalysisTypeSource label="Source" />
      </Tab>
      <CustomTab />
    </TabbedShowLayout>
  </Show>
);
